import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { AreaChart } from "@/shared/ui/AreaChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { Link, useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import {
  ClockIcon,
  DownloadIcon,
  GraduationCapIcon,
  PackageIcon,
  TrendingUpIcon,
  UserCheckIcon,
  UserMinusIcon,
  UserPlusIcon,
  UsersIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import {
  useStudentPackagesReportQuery,
  useStudentReportLowRemainingQuery,
  useStudentReportNewTrendQuery,
  useStudentReportSummaryQuery,
  useStudentReportUtilizationQuery,
} from "../api/queries";
import { exportStudentPackagesReportCsv } from "../lib/student-packages-export";
import {
  StudentReportFilter,
  type StudentReportSearch,
} from "../ui/StudentReportFilter";
import { StudentPackagesReportTable } from "../ui/StudentPackagesReportTable";
import { StudentReportLowRemainingTable } from "../ui/StudentReportLowRemainingTable";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "../ui/ReportSummaryStatCard";

function useReportQuery(search: StudentReportSearch) {
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const dateFrom = search.dateFrom ?? defaultFrom;
  const dateTo = search.dateTo ?? defaultTo;

  const summaryQuery = useMemo(
    () => ({
      dateFrom,
      dateTo,
      groupId: search.groupId,
    }),
    [dateFrom, dateTo, search.groupId],
  );

  const utilizationQuery = useMemo(
    () => ({
      groupId: search.groupId,
      packageType: (search.packageType ?? "all") as
        | "package"
        | "private_lesson"
        | "all",
    }),
    [search.groupId, search.packageType],
  );

  const newTrendQuery = useMemo(
    () => ({
      dateFrom,
      dateTo,
      groupBy: (search.groupBy ?? "day") as "day" | "month",
      groupId: search.groupId,
    }),
    [dateFrom, dateTo, search.groupId, search.groupBy],
  );

  const lowRemainingQuery = useMemo(
    () => ({
      groupId: search.groupId,
      packageType: (search.packageType ?? "all") as
        | "package"
        | "private_lesson"
        | "all",
      thresholdSessions: search.thresholdSessions ?? 3,
      thresholdHours: search.thresholdHours ?? 2,
      limit: 30,
    }),
    [
      search.groupId,
      search.packageType,
      search.thresholdSessions,
      search.thresholdHours,
    ],
  );

  return {
    dateFrom,
    dateTo,
    summaryQuery,
    utilizationQuery,
    newTrendQuery,
    lowRemainingQuery,
    packagesReportQuery: {
      dateFrom,
      dateTo,
      groupId: search.groupId,
      packageType: (search.packageType ?? "all") as
        | "package"
        | "private_lesson"
        | "all",
      limit: 5000,
    },
  };
}

const newTrendChartConfig: ChartConfig = {
  period: { label: "ວັນທີ" },
  count: { label: "ນັກຮຽນໃໝ່", color: "var(--color-chart-1)" },
};

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function StudentReportPage() {
  const search = useSearch({
    from: "/app/reports/students",
  }) as StudentReportSearch;

  const { summaryQuery, utilizationQuery, newTrendQuery, lowRemainingQuery, packagesReportQuery } =
    useReportQuery(search);

  const { data: summary, isLoading: summaryLoading } =
    useStudentReportSummaryQuery(summaryQuery);
  const { data: utilization, isLoading: utilizationLoading } =
    useStudentReportUtilizationQuery(utilizationQuery);
  const { data: newTrend = [] } = useStudentReportNewTrendQuery(newTrendQuery);
  const { data: lowRemaining = [], isLoading: lowRemainingLoading } =
    useStudentReportLowRemainingQuery(lowRemainingQuery);
  const { data: packagesReport = [], isLoading: packagesReportLoading } =
    useStudentPackagesReportQuery(packagesReportQuery);

  const trendChartData = useMemo(
    () =>
      newTrend.map((r) => ({
        period: r.period,
        count: r.count,
      })),
    [newTrend],
  );

  const exportTrendCsv = useCallback(() => {
    const header = "ວັນທີ,ນັກຮຽນໃໝ່";
    const rows = newTrend.map((r) => `${r.period},${r.count}`);
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `student-report-new-trend-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [newTrend]);

  const exportLowRemainingCsv = useCallback(() => {
    const header = "ນັກຮຽນ,ລະຫັດສະມາຊິກ,ແພັກເກດ,ປະເພດ,ຄົງເຫຼືອ,ທັງໝົດ";
    const rows = lowRemaining.map((r) =>
      r.packageType === "package"
        ? `${escapeCsvCell(r.studentName)},${escapeCsvCell(r.memberId)},${escapeCsvCell(r.packageName)},ແພັກເກດຄລາດສ໌,${escapeCsvCell(r.remainingSessions ?? 0)},${escapeCsvCell(r.totalSessions ?? 0)}`
        : `${escapeCsvCell(r.studentName)},${escapeCsvCell(r.memberId)},${escapeCsvCell(r.packageName)},ແພັກເກດຮຽນສ່ວນຕົວ,${escapeCsvCell(r.remainingHours ?? 0)},${escapeCsvCell(r.totalHours ?? 0)}`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `student-report-low-remaining-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [lowRemaining]);

  const exportPackagesReportCsv = useCallback(() => {
    exportStudentPackagesReportCsv(packagesReport);
  }, [packagesReport]);

  const studentsListFilters = useMemo(() => {
    if (!search.groupId) return undefined;
    return [{ field: "groupId", op: "eq" as const, value: search.groupId }];
  }, [search.groupId]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລາຍງານນັກຮຽນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານນັກຮຽນ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ສະແດງຈຳນວນນັກຮຽນໃໝ່ ກຸ່ມໃຊ້ງານ ແລະການໃຊ້ງານແພັກເກດ
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/app/students"
              search={{ filters: studentsListFilters }}
              className="text-muted-foreground text-sm underline hover:text-foreground"
            >
              ໄປຫນ້າລາຍການນັກຮຽນ
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={exportPackagesReportCsv}
              disabled={packagesReport.length === 0}
            >
              <DownloadIcon className="mr-2 size-4" />
              CSV ແພັກເກດນັກຮຽນ
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportTrendCsv}
              disabled={newTrend.length === 0}
            >
              <DownloadIcon className="mr-2 size-4" />
              CSV ແນວໂນ້ມ
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportLowRemainingCsv}
              disabled={lowRemaining.length === 0}
            >
              <DownloadIcon className="mr-2 size-4" />
              CSV ເຫຼືອນ້ອຍ
            </Button>
          </div>
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <StudentReportFilter />
        </div>

        <ReportSummaryStatGrid columns={4}>
          <ReportSummaryStatCard
            title="ນັກຮຽນທັງໝົດ"
            icon={UsersIcon}
            tone="students"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.totalStudents ?? 0)}
          />
          <ReportSummaryStatCard
            title="ນັກຮຽນໃໝ່ໃນຊ່ວງນີ້"
            icon={UserPlusIcon}
            tone="revenue"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.newStudentsCount ?? 0)}
          />
          <ReportSummaryStatCard
            title="ນັກຮຽນທີ່ໃຊ້ງານຢູ່"
            icon={UserCheckIcon}
            tone="attendance"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.activeStudentsCount ?? 0)}
          />
          <ReportSummaryStatCard
            title="ນັກຮຽນທີ່ປິດການໃຊ້ງານ"
            icon={UserMinusIcon}
            tone="refunds"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.inactiveStudentsCount ?? 0)}
          />
          <ReportSummaryStatCard
            title="ເວັ້ນໄຊຊັນຄົງເຫຼືອໂດຍສະເລ່ຍ"
            icon={GraduationCapIcon}
            tone="packages"
            isLoading={utilizationLoading}
            value={
              utilization?.avgRemainingSessions != null
                ? utilization.avgRemainingSessions.toFixed(1)
                : "—"
            }
          />
          <ReportSummaryStatCard
            title="ເວລາຄົງເຫຼືອໂດຍສະເລ່ຍ"
            icon={ClockIcon}
            tone="attendance"
            isLoading={utilizationLoading}
            value={
              utilization?.avgRemainingHours != null ? (
                <>
                  {utilization.avgRemainingHours.toFixed(1)}{" "}
                  <span className="font-normal text-muted-foreground text-sm">
                    ຊົ່ວໂມງ
                  </span>
                </>
              ) : (
                "—"
              )
            }
          />
          <ReportSummaryStatCard
            title="ແພັກເກດຄລາດສ໌ (active)"
            icon={PackageIcon}
            tone="packages"
            isLoading={utilizationLoading}
            value={reportSummaryCount(utilization?.packageCount ?? 0)}
          />
          <ReportSummaryStatCard
            title="ແພັກເກດຮຽນສ່ວນຕົວ (active)"
            icon={PackageIcon}
            tone="students"
            isLoading={utilizationLoading}
            value={reportSummaryCount(
              utilization?.privateLessonPackageCount ?? 0,
            )}
          />
        </ReportSummaryStatGrid>

        {trendChartData.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUpIcon className="size-5" />
                ແນວໂນ້ມນັກຮຽນໃໝ່
              </CardTitle>
              <p className="text-muted-foreground text-sm">
                ຈຳນວນນັກຮຽນທີ່ສະໝັກໃໝ່ໃນແຕ່ລະວັນ
              </p>
            </CardHeader>
            <CardContent>
              <AreaChart
                data={trendChartData}
                config={newTrendChartConfig}
                xDataKey="period"
                valueDataKey="count"
                xAxisTickFormatter={(v) =>
                  typeof v === "string" ? v.slice(0, 10) : String(v)
                }
                className="mx-auto aspect-video max-h-[300px]"
              />
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>ລາຍງານແພັກເກດນັກຮຽນ</CardTitle>
            <p className="text-muted-foreground text-sm">
              ແພັກເກດທີ່ມີການຈ່າຍໃນຊ່ວງວັນທີທີ່ເລືອກ — ກັ່ນຕອງຕາມກຸ່ມ ແລະ ປະເພດແພັກເກດ (ມີຜົນກັບ CSV ດ້ວຍ).
              ຍອດລວມແຖວ「ແພັກເກດຄລາດສ໌」ຄວນຕົງກັບ「ລາຍຮັບແພັກເກດ」ໃນລາຍງານລາຍຮັບ (ລວມທຸກ template, ບໍ່ແມ່ນແຕ່ Pro 1).
            </p>
          </CardHeader>
          <CardContent>
            <StudentPackagesReportTable
              isLoading={packagesReportLoading}
              data={packagesReport}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>ນັກຮຽນທີ່ແພັກເກດເຫຼືອນ້ອຍ</CardTitle>
            <p className="text-muted-foreground text-sm">
              ເວັ້ນໄຊຊັນ &lt; {search.thresholdSessions ?? 3} ຫຼື ຊົ່ວໂມງ &lt;{" "}
              {search.thresholdHours ?? 2} ຄົງເຫຼືອ
            </p>
          </CardHeader>
          <CardContent>
            <StudentReportLowRemainingTable
              isLoading={lowRemainingLoading}
              data={lowRemaining}
            />
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
